import {
  RUTERO_BOOKING_LIST_CREATED_BY_PATTERN,
  RUTERO_BOOKING_LIST_LIMITS,
  UserRoles,
} from '@bandido/shared/types';

/**
 * EL ÁMBITO del listado: de quién son las reservas que se están mirando.
 *
 * No es un filtro más — es un SELECTOR que condiciona todo lo que la pantalla
 * dice, contadores de pestañas incluidos: en "Mías" el badge cuenta MIS
 * pendientes, no los de la agencia. Por eso vive acá y no entre los filtros
 * facetados, y por eso viaja en la query key (§21.2): servir la cache de otro
 * ámbito le mostraría al vendedor la cola del operador.
 *
 * Del lado del contrato es un `createdBy` (`ListRuteroBookingsDto`), que NUNCA
 * amplía nada: la agencia sale del JWT del otro lado (§21.1).
 */
export type BookingListScopeKind = 'mine' | 'all' | 'advisor';

/** El asesor elegido: el id viaja, el rótulo solo se dibuja. */
export interface BookingListAdvisor {
  id: string;
  label: string;
}

export interface BookingListScope {
  kind: BookingListScopeKind;
  /** Presente SOLO con `advisor`. */
  advisor?: BookingListAdvisor;
}

export const MINE_BOOKING_LIST_SCOPE: BookingListScope = { kind: 'mine' };
export const ALL_BOOKING_LIST_SCOPE: BookingListScope = { kind: 'all' };

/** Lo que se guarda del rótulo: es display, no identidad. */
const MAX_ADVISOR_LABEL = 120;

/**
 * Un `createdBy` que el endpoint va a aceptar. Se valida con el MISMO patrón y
 * el MISMO tope que el DTO del borde (§21.22): así un id guardado por una
 * versión anterior —o editado a mano en el storage— se descarta acá en vez de
 * volver como un 400 que la pantalla no sabría explicar.
 */
export function isBookingListAdvisorId(id: unknown): id is string {
  return (
    typeof id === 'string' &&
    id.length <= RUTERO_BOOKING_LIST_LIMITS.createdByMaxLength &&
    RUTERO_BOOKING_LIST_CREATED_BY_PATTERN.test(id)
  );
}

/**
 * Qué `createdBy` mandar, o que todavía no se puede pedir nada.
 *
 * Los tres estados son distintos y no se colapsan en `undefined` (§21.18.4):
 * "sin createdBy" ES el ámbito de toda la agencia, así que un "Mías" cuyo
 * usuario todavía no cargó no puede degradar a eso — mostraría las reservas de
 * todos con el selector diciendo "Mías". Mientras no se resuelve, la pantalla
 * no consulta.
 */
export type BookingListScopeResolution =
  | { status: 'ready'; createdBy?: string }
  | { status: 'unresolved' };

export function resolveBookingListScope(
  scope: BookingListScope,
  currentUserId?: string,
): BookingListScopeResolution {
  if (scope.kind === 'all') return { status: 'ready' };
  if (scope.kind === 'advisor') {
    return isBookingListAdvisorId(scope.advisor?.id)
      ? { status: 'ready', createdBy: scope.advisor?.id }
      : { status: 'unresolved' };
  }
  return isBookingListAdvisorId(currentUserId)
    ? { status: 'ready', createdBy: currentUserId }
    : { status: 'unresolved' };
}

/**
 * El ámbito con el que entra cada rol la primera vez: el vendedor a lo suyo,
 * quien opera para toda la agencia a todo. Un usuario con los dos roles ve lo
 * más amplio — puede acotarlo con un toque, y al revés se perdería trabajo
 * pendiente sin enterarse.
 */
export function defaultBookingListScope(roles?: string): BookingListScope {
  const list = (roles ?? '')
    .split(',')
    .map((role) => role.trim().toUpperCase())
    .filter(Boolean);
  const wide =
    list.includes(UserRoles.ADMINS) || list.includes(UserRoles.OPERATORS);
  return wide ? ALL_BOOKING_LIST_SCOPE : MINE_BOOKING_LIST_SCOPE;
}

/**
 * La preferencia es POR USUARIO: la app no recarga al cambiar de sesión, así
 * que sin el id en la clave el que entra después heredaría el ámbito del
 * anterior (mismo criterio que el draft de respuestas del operador).
 */
export function bookingListScopeStorageKey(userId: string): string {
  return `rutero:bookingsScope:${userId}`;
}

/** Contenido de storage: puede venir de otra versión o editado a mano. */
function parseScope(raw: unknown): BookingListScope | null {
  if (!raw || typeof raw !== 'object') return null;
  const { kind } = raw as BookingListScope;
  if (kind === 'mine' || kind === 'all') return { kind };
  if (kind !== 'advisor') return null;
  const { advisor } = raw as BookingListScope;
  if (!isBookingListAdvisorId(advisor?.id)) return null;
  return {
    kind: 'advisor',
    advisor: {
      id: advisor?.id as string,
      label:
        typeof advisor?.label === 'string'
          ? advisor.label.slice(0, MAX_ADVISOR_LABEL)
          : (advisor?.id as string),
    },
  };
}

export function loadBookingListScope(userId?: string): BookingListScope | null {
  if (!userId) return null;
  try {
    const raw = localStorage.getItem(bookingListScopeStorageKey(userId));
    return raw ? parseScope(JSON.parse(raw)) : null;
  } catch {
    // Storage bloqueado o JSON roto: se entra con el default del rol.
    return null;
  }
}

export function persistBookingListScope(
  userId: string | undefined,
  scope: BookingListScope,
): void {
  if (!userId) return;
  try {
    localStorage.setItem(
      bookingListScopeStorageKey(userId),
      JSON.stringify(scope),
    );
  } catch {
    /* sin memoria: el ámbito vale para esta sesión igual */
  }
}
